/**
 * @file Database-driven target validation
 * @module targeting/isTarget
 */

import { getDeployTargetingRules, getStrategemTargetingRules, getDroneTargetingRules } from './targetingArrays';
import { canTargetUnit } from './targetingValidation';

/**
 * Check if a unit can attack drones
 * @param {Object} attackerUnit - The attacking unit
 * @returns {boolean} True if the unit can attack drones
 */
function canAttackDrones(attackerUnit) {
  if (!attackerUnit) return false;
  
  // CRITICAL: By default, units CANNOT attack drones
  // Only units with special abilities or effects that explicitly allow attacking drones can do so
  // Add unit IDs or abilities here that allow attacking drones
  const unitsThatCanAttackDrones = []; // Currently no units can attack drones via normal attacks
  const abilitiesThatAllowAttackingDrones = []; // Currently no abilities allow attacking drones
  
  // Check if unit has explicit permission to attack drones
  if (unitsThatCanAttackDrones.includes(attackerUnit.id)) {
    return true;
  }
  
  // Check if unit has an ability that allows attacking drones
  const unitAbilities = attackerUnit.abilities || [];
  const instanceAbilities = attackerUnit.instanceAbilities || [];
  const allAbilities = [...unitAbilities, ...instanceAbilities];
  
  if (allAbilities.some(ability => abilitiesThatAllowAttackingDrones.includes(ability))) {
    return true;
  }
  
  // By default, units cannot attack drones
  return false;
}

/**
 * Determine if a unit should be highlighted as a target based on database rules
 * @param {Object} params - Targeting parameters
 * @param {boolean} params.strategemTargetMode - Whether strategem targeting is active
 * @param {boolean} params.immobilizeTargetMode - Whether immobilize targeting is active
 * @param {boolean} params.confuseTargetMode - Whether confuse targeting is active
 * @param {boolean} params.weaponTargetMode - Whether weapon targeting is active
 * @param {boolean} params.deployTargetMode - Whether deploy targeting is active
 * @param {number} params.selectedDeployOrderId - ID of the order being deployed
 * @param {string} params.selectedDeployZone - Zone where the order is being deployed
 * @param {string} params.zone - Zone being checked
 * @param {Object} params.targetingData - Database targeting data
 * @param {Object} params.gameState - Current game state
 * @param {string} params.playerId - Current player ID
 * @param {Object} params.unit - The unit being checked (optional)
 * @param {boolean} params.attackMode - Whether attack mode is active
 * @param {Object} params.selectedAttacker - The selected attacker {zone, idx}
 * @param {boolean} params.droneTargetMode - Whether drone targeting is active
 * @param {number} params.selectedDroneId - ID of the drone being activated
 * @returns {boolean} True if the unit should be highlighted as a target
 */
function isTarget({
  strategemTargetMode,
  immobilizeTargetMode,
  confuseTargetMode,
  weaponTargetMode,
  deployTargetMode,
  selectedDeployOrderId,
  selectedDeployInstanceId,
  selectedDeployZone,
  zone,
  targetingData,
  gameState,
  playerId,
  unit,
  attackMode,
  selectedAttacker,
  droneTargetMode,
  selectedDroneId
}) {
  // Tutorial overlay config: hide targets when body has data-tutorial-hide-* (from missionId_tutorial_ui.json)
  const selectedStrategemIdParam = arguments[0].selectedStrategemId;
  if (typeof document !== 'undefined' && document.body) {
    const body = document.body;
    const zoneNorm = (zone || '').replace('opponent_', '');
    const unitOrderIdForHide = unit ? (unit.id ?? unit.orderId) : null;
    const overlayStratAttrEarly = body.getAttribute('data-tutorial-overlay-strategem-restrict');
    let isOverlayAllowedUnit = false;
    if (strategemTargetMode && unitOrderIdForHide != null && overlayStratAttrEarly && selectedStrategemIdParam) {
      try {
        const overlay = JSON.parse(overlayStratAttrEarly);
        const allowed = overlay[String(selectedStrategemIdParam)] ?? overlay[selectedStrategemIdParam];
        if (Array.isArray(allowed) && allowed.length > 0) {
          isOverlayAllowedUnit = new Set(allowed.map(a => Number(a))).has(Number(unitOrderIdForHide));
        }
      } catch (_) {}
    }
    // Zone hide attrs are for earlier deploy/attack lesson visuals; they must not suppress strategem unit picks
    // (Mission 1: Guided Missile → Panicked Overseer in flank while flank was “hidden” during deploy).
    if (!isOverlayAllowedUnit && !strategemTargetMode) {
      if (zoneNorm === 'flank' && body.getAttribute('data-tutorial-hide-zone-flank') === 'true') return false;
      if (zoneNorm === 'front' && body.getAttribute('data-tutorial-hide-zone-front') === 'true') return false;
    }
    if (unit && (unit.id != null || unit.orderId != null)) {
      const uid = unit.id ?? unit.orderId;
      if (zone && zone.startsWith('opponent') && body.getAttribute(`data-tutorial-hide-opponent-unit-${uid}`) === 'true') return false;
      if (zone && !zone.startsWith('opponent') && body.getAttribute(`data-tutorial-hide-friendly-unit-${uid}`) === 'true') return false;
    }
    // Opponent/friendly general: zone is often opponent_general or player_general - hide if attribute set
    if (zone === 'opponent_general' && body.getAttribute('data-tutorial-hide-opponent-general') === 'true') return false;
    if (zone === 'player_general' && body.getAttribute('data-tutorial-hide-friendly-general') === 'true') return false;
  }

  // Dev override: force highlight all enemy units
  try {
    if (typeof window !== 'undefined' && window.FORCE_HIGHLIGHT_ENEMY && unit) {
      const selfId = String(playerId);
      const opponentId = Object.keys(gameState.state || {}).find(id => id !== selfId);
      const normalizedZone = (zone || '').replace('opponent_', '');
      const enemyUnits = (gameState.state[opponentId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
      const enemyInstanceIds = enemyUnits.map(u => u.instanceId).filter(Boolean);
      return unit.instanceId ? enemyInstanceIds.includes(unit.instanceId) : enemyUnits.some(u => u && u.id === unit.id);
    }
  } catch (_) {}
  // Handle strategem targeting mode
  const overlayStrategemRestrictAttr =
    typeof document !== 'undefined' ? document.body.getAttribute('data-tutorial-overlay-strategem-restrict') : null;
  if (
    strategemTargetMode &&
    arguments[0].selectedStrategemId &&
    (targetingData || overlayStrategemRestrictAttr)
  ) {
    // Tutorial 4 strategem target restriction: only allow targeting specific units
    const t4StratAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial4-strategem-target-restrict');
    let t4OverridePlayer = false;
    if (t4StratAttr && unit) {
      try {
        const t4 = JSON.parse(t4StratAttr);
        if (t4.strategemId && arguments[0].selectedStrategemId === t4.strategemId) {
          if (t4.targetOrderId && unit.id !== t4.targetOrderId) return false;
          if (t4.targetOrderId && unit.id === t4.targetOrderId) t4OverridePlayer = true;
        }
      } catch (_) {}
    }
    // Tutorial overlay: strategemTargetAllowOnly — only allow targeting listed order IDs; when unit IS in list, override player restriction (e.g. Precision Strike → Malignant Berserker 377)
    let overlayStrategemAllowOverride = false;
    const overlayStratAttr = overlayStrategemRestrictAttr;
    const unitOrderId = unit ? (unit.id ?? unit.orderId) : null;
    if (overlayStratAttr && unit && unitOrderId != null) {
      try {
        const overlay = JSON.parse(overlayStratAttr);
        const strategemId = arguments[0].selectedStrategemId;
        const allowed = overlay[String(strategemId)] ?? overlay[strategemId];
        if (Array.isArray(allowed) && allowed.length > 0) {
          const allowedSet = new Set(allowed.map(a => Number(a)));
          if (!allowedSet.has(Number(unitOrderId))) return false;
          overlayStrategemAllowOverride = true;
        }
      } catch (_) {}
    }
    // Tutorial overlay allow-list must win before DB rules: missing strategem row or unloaded targetingData
    // would otherwise return false and block the allowed unit (e.g. Guided Missile → Panicked Overseer).
    if (overlayStrategemAllowOverride && unit && unit.instanceId) {
      console.log('🎯 isTarget: Strategem can target unit (overlay allow)', unit.name, unit.id);
      return true;
    }
    if (!targetingData) {
      console.log('🎯 isTarget: Strategem targeting needs targetingData (none loaded)', arguments[0].selectedStrategemId);
      return false;
    }
    const targetingRules = getStrategemTargetingRules(arguments[0].selectedStrategemId, targetingData);
    
    if (!targetingRules) {
      console.log('🎯 isTarget: No targeting rules for strategem', arguments[0].selectedStrategemId);
      return false;
    }
    
    if (targetingRules.targeting === 'none') return false;

    // Generals (HUD zones player_general / opponent_general) — rules use general + player columns
    if (unit?.isGeneral === true) {
      if (targetingRules.general !== true) return false;
      const pr = targetingRules.player || 'opponent';
      if (zone === 'opponent_general' && (pr === 'opponent' || pr === 'both')) return true;
      if (zone === 'player_general' && (pr === 'friendly' || pr === 'both')) return true;
      return false;
    }
    
    console.log('🎯 isTarget: Strategem targeting mode active:', {
      strategemId: arguments[0].selectedStrategemId,
      targetingRules,
      zone,
      unit: unit ? unit.name : 'no unit',
      playerId
    });
    
    // Check if this is zone targeting
    if (targetingRules.targeting === 'zone') {
      // Zone targeting - don't highlight individual units
      if (unit) {
        return false;
      }
      // Zones will be highlighted by a different mechanism
      return false;
    }
    
    // Unit targeting - check player restrictions (deployed orders only; drones use branch below)
    if (targetingRules.targeting === 'single unit' && unit && unit.type !== 'drone') {
      // Check if unit has instanceId
      if (!unit.instanceId) return false;
      
      // Unit 96 (Hardshell Defender), Unit 262 (Apep), and Unit 384 (Nehebkau) cannot be targeted by strategems
      // But they can still be affected by area-effect strategems (no targeting required)
      // NOTE: Confused units CAN be targeted (confuse removes all special effects including specialAbility)
      if (unit.id === 96 || unit.id === 262 || unit.id === 384) {
        // Confused units lose all special effects and can be targeted
        if (!unit.confused && !unit.isConfused) {
          const unitName = unit.id === 96 ? 'Hardshell Defender' : 
                          unit.id === 262 ? 'Apep' : 
                          'Nehebkau';
          console.log(`🛡️ isTarget: Unit ${unit.id} (${unitName}) cannot be targeted by strategems`);
          return false;
        }
        // If confused, allow targeting (return true, continue with normal targeting checks)
      }
      
      // Get player alignment
      const selfId = String(playerId);
      const opponentId = Object.keys(gameState.state || {}).find(id => id !== selfId);
      const normalizedZone = (zone || '').replace('opponent_', '');
      const friendlyUnits = (gameState.state[selfId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
      const enemyUnits = (gameState.state[opponentId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
      const isFriendly = friendlyUnits.some(u => u.instanceId === unit.instanceId);
      const isEnemy = enemyUnits.some(u => u.instanceId === unit.instanceId);
      const unitPlayer = isFriendly ? 'friendly' : (isEnemy ? 'opponent' : 'unknown');
      
      // Check player restrictions (tutorial overrides allow targeting the specific unit regardless of friendly/opponent)
      if (!t4OverridePlayer && !overlayStrategemAllowOverride) {
        if (targetingRules.player === 'friendly' && unitPlayer !== 'friendly') {
          console.log('🎯 isTarget: Strategem requires friendly target but unit is', unitPlayer);
          return false;
        }
        if (targetingRules.player === 'opponent' && unitPlayer !== 'opponent') {
          console.log('🎯 isTarget: Strategem requires opponent target but unit is', unitPlayer);
          return false;
        }
      }
      // 'both' allows any unit
      
      // ✅ OOR units CAN be targeted by strategems (OOR only prevents attacks, not non-attack effects like shields)
      // Note: isOOR status is passed through for visual display but doesn't block targeting
      
      console.log('🎯 isTarget: Strategem can target unit', unit.name, unit.isOOR ? '(OOR - but can still be targeted for non-attack effects)' : '');
      return true;
    }
    
    // Drone targeting - check if this is a drone and strategem can target drones
    if (targetingRules.targeting === 'single unit' && unit && unit.type === 'drone') {
      if (Number(arguments[0].selectedStrategemId) === 60) {
        return false;
      }
      // Check if unit has instanceId
      if (!unit.instanceId) return false;
      
      // Get player alignment for drones
      const selfId = String(playerId);
      const opponentId = Object.keys(gameState.state || {}).find(id => id !== selfId);
      const friendlyDrones = (gameState.state[selfId]?.drones || []).filter(Boolean);
      const enemyDrones = (gameState.state[opponentId]?.drones || []).filter(Boolean);
      const isFriendlyDrone = friendlyDrones.some(d => d.instanceId === unit.instanceId);
      const isEnemyDrone = enemyDrones.some(d => d.instanceId === unit.instanceId);
      const dronePlayer = isFriendlyDrone ? 'friendly' : (isEnemyDrone ? 'opponent' : 'unknown');
      
      // Check player restrictions
      if (targetingRules.player === 'friendly' && dronePlayer !== 'friendly') {
        console.log('🎯 isTarget: Strategem requires friendly drone but drone is', dronePlayer);
        return false;
      }
      if (targetingRules.player === 'opponent' && dronePlayer !== 'opponent') {
        console.log('🎯 isTarget: Strategem requires opponent drone but drone is', dronePlayer);
        return false;
      }
      // 'both' allows any drone
      
      console.log('🎯 isTarget: Strategem can target drone', unit.name);
      return true;
    }
    
    return true;
  }

  // Handle weapon deploy targeting mode
  if (arguments[0].weaponDeployTargetMode) {
    // Weapon deploy effects can target any unit in any zone
    return true;
  }

  // Handle attack mode - highlight opponent units and general as targets
  if (attackMode && selectedAttacker && unit) {
    // Tutorial 4 attack restriction: only allow specific attackers to target specific units
    const t4AttackAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial4-attack-restrict');
    if (t4AttackAttr) {
      try {
        const t4 = JSON.parse(t4AttackAttr);
        const selfId = String(playerId);
        const attackerUnit = gameState.state?.[selfId]?.playedOrders?.[selectedAttacker.zone]?.[selectedAttacker.idx];
        if (attackerUnit && t4.attackers && !t4.attackers.includes(attackerUnit.id)) return false;
        if (unit && t4.targets && !t4.targets.includes(unit.id)) return false;
      } catch (_) {}
    }
    // Tutorial overlay attack restrict (e.g. Kill threats): only allow listed attackers (if set) and targets (if set)
    // When both checks pass, return true immediately so zone/OOR/Protect rules don't block the tutorial target
    const overlayAttackAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial-overlay-attack-restrict');
    if (overlayAttackAttr) {
      try {
        const o = JSON.parse(overlayAttackAttr);
        let attackerOk = true;
        if (o.attackers && Array.isArray(o.attackers) && o.attackers.length > 0) {
          const selfId = String(playerId);
          const attackerUnit = gameState.state?.[selfId]?.playedOrders?.[selectedAttacker.zone]?.[selectedAttacker.idx];
          const attackerId = attackerUnit?.id ?? attackerUnit?.orderId;
          attackerOk = attackerId != null && o.attackers.map(a => Number(a)).includes(Number(attackerId));
        }
        let targetOk = true;
        if (o.targets && Array.isArray(o.targets) && o.targets.length > 0 && unit) {
          const targetOrderId = Number(unit.id ?? unit.orderId);
          targetOk = !Number.isNaN(targetOrderId) && o.targets.map(t => Number(t)).includes(targetOrderId);
        }
        if (!attackerOk || !targetOk) return false;
        // Overlay restrict satisfied: allow this target (skip zone/OOR/Protect checks below)
        return true;
      } catch (_) {}
    }
    // Tutorial overlay: restrict attack targets to attackableOpponentZone order IDs
    const overlayTargetingAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial-overlay-targeting');
    if (overlayTargetingAttr) {
      try {
        const overlay = JSON.parse(overlayTargetingAttr);
        const allowed = overlay.attackableOpponentZone;
        if (Array.isArray(allowed) && allowed.length > 0 && unit) {
          const unitOrderId = Number(unit.id ?? unit.orderId);
          if (!Number.isNaN(unitOrderId) && !allowed.map(a => Number(a)).includes(unitOrderId)) return false;
        }
      } catch (_) {}
    }

    // Opponent general attack — mirrors combat resolveGeneralAttack (Protect in attacker's zone, attack rating > 0)
    if (unit?.isGeneral === true && (zone === 'opponent_general' || zone === 'general')) {
      const selfId = String(playerId);
      const stateIds = Object.keys(gameState.state || {});
      const opponentId = stateIds.find(id => id !== selfId);
      const attackerUnit = gameState.state?.[selfId]?.playedOrders?.[selectedAttacker.zone]?.[selectedAttacker.idx];
      if (!attackerUnit) return false;
      const attackRating = attackerUnit.attack_rating ?? attackerUnit.damage ?? 0;
      if (attackRating <= 0) return false;

      const opponentState = opponentId ? gameState.state?.[opponentId] : null;
      const attackerZone = selectedAttacker.zone;
      const protectUnitsInZone =
        opponentState?.playedOrders?.[attackerZone]?.filter((u) => u?.abilities?.includes('Protect')) || [];
      const attackerIgnoresProtect =
        !attackerUnit.confused &&
        !attackerUnit.isConfused &&
        (attackerUnit.abilities?.includes('IgnoreProtect') ||
          attackerUnit.attackEffect === 'ignore_protect_any_zone');

      if (protectUnitsInZone.length > 0 && !attackerIgnoresProtect) {
        return false;
      }
      return true;
    }

    if (unit?.isGeneral === true && zone === 'player_general') {
      return false;
    }

    const selfId = String(playerId);
    const attackerUnit = gameState.state?.[selfId]?.playedOrders?.[selectedAttacker.zone]?.[selectedAttacker.idx];
    // Tutorial overlay: attacker must have a weapon attached before they can attack (e.g. "Finish them")
    const attackRequireWeaponAttr = typeof document !== 'undefined' && document.body && document.body.getAttribute('data-tutorial-overlay-attack-require-weapon-attached');
    if (attackRequireWeaponAttr && attackerUnit && attackerUnit.id != null) {
      try {
        const requireWeaponIds = JSON.parse(attackRequireWeaponAttr);
        if (Array.isArray(requireWeaponIds) && requireWeaponIds.length > 0) {
          const set = new Set(requireWeaponIds.map(a => Number(a)));
          if (set.has(Number(attackerUnit.id))) {
            const weaponList = attackerUnit.weapons || attackerUnit.attachedWeapons;
            const hasWeapon = Array.isArray(weaponList) && weaponList.length > 0;
            if (!hasWeapon) return false;
          }
        }
      } catch (_) {}
    }
    // Check if this is an opponent unit
    const stateIds = Object.keys(gameState.state || {});
    const opponentId = stateIds.find(id => id !== selfId);
    const normalizedZone = (zone || '').replace('opponent_', '');
    const isOpponentZone = zone.startsWith('opponent_');
    
    // Attacker unit already resolved above for overlay weapon check
    const hasAttackAnyZone = attackerUnit?.abilities?.includes('AttackAnyZone') || 
                            attackerUnit?.instanceAbilities?.includes('AttackAnyZone') ||
                            attackerUnit?.attackEffect === 'ignore_protect_any_zone';
    const attackerZone = selectedAttacker.zone; // e.g., "front" or "flank"
    
    // Check if this is a drone (drones are stored separately from playedOrders)
    if (unit.type === 'drone' || zone === 'opponent_drones' || zone === 'drones') {
      // ✅ FIX: Check if the attacker can actually attack drones
      if (!canAttackDrones(attackerUnit)) {
        console.log(`🎯 isTarget: ${attackerUnit?.name} (ID: ${attackerUnit?.id}) cannot attack drones`);
        return false;
      }
      
      // Check if drone is in opponent's drones array
      const opponentDrones = (gameState.state[opponentId]?.drones || []).filter(Boolean);
      const isOpponentDrone = opponentDrones.some(d => d.instanceId === unit.instanceId);
      
      if (isOpponentDrone) {
        // Drones can be attacked (no OOR check needed for drones)
        // Note: Drones don't have zone restrictions - they can be attacked from any zone
        return true;
      }
      // Not an opponent drone, don't highlight
      return false;
    }
    
    // Check regular units in playedOrders
    if (isOpponentZone) {
      // Check if unit is in opponent's playedOrders
      const opponentUnits = (gameState.state[opponentId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
      const isOpponentUnit = opponentUnits.some(u => u.instanceId === unit.instanceId);
      
      if (isOpponentUnit) {
        // OOR units cannot be attacked
        if (unit.isOOR) {
          return false;
        }
        
        // ✅ ZONE RESTRICTION: Units can only attack units in the same zone (unless they have AttackAnyZone)
        // Exception: Units with AttackAnyZone ability (e.g., Unit 75 - T-Rex) can attack any zone
        if (!hasAttackAnyZone && attackerZone !== normalizedZone) {
          // Attacker cannot attack units in different zone
          return false;
        }
        
        // ✅ PROTECT CHECK: If Protect units exist in the target zone, only Protect units can be targeted
        // Exception: Units with IgnoreProtect ability (e.g., Unit 75 - T-Rex) can bypass Protect
        const attackerIgnoresProtect = attackerUnit?.abilities?.includes('IgnoreProtect') || 
                                      attackerUnit?.attackEffect === 'ignore_protect_any_zone';
        
        if (!attackerIgnoresProtect) {
          // Check if there are Protect units in the target zone
          const opponentState = gameState.state?.[opponentId];
          const protectUnitsInZone = opponentState?.playedOrders?.[normalizedZone]?.filter(u => 
            u && u.abilities?.includes('Protect')
          ) || [];
          
          // If Protect units exist, only allow targeting Protect units
          if (protectUnitsInZone.length > 0) {
            const unitHasProtect = unit.abilities?.includes('Protect');
            if (!unitHasProtect) {
              // This unit doesn't have Protect, but Protect units exist - don't highlight
              return false;
            }
          }
        }
        
        // Valid attack target
        return true;
      }
    }
    // Not an opponent unit, don't highlight
    return false;
  }

  // Handle drone targeting mode
  if (droneTargetMode && selectedDroneId && targetingData && unit) {
    // Campaign Mission 3: restrict drone to only target Dark Circuit Engineer (47) during "Use Your Drone"
    const droneTargetRestriction = typeof document !== 'undefined' && document.body.getAttribute('data-drone-target-only');
    if (droneTargetRestriction) {
      const allowedId = parseInt(droneTargetRestriction, 10);
      const unitOrderId = Number(unit.id ?? unit.orderId);
      if (Number.isNaN(unitOrderId) || unitOrderId !== allowedId) return false;
    }

    // Get targeting rules for the drone
    const targetingRules = getDroneTargetingRules(selectedDroneId, targetingData);
    
    if (!targetingRules) {
      console.log('🎯 isTarget: No targeting rules for drone', selectedDroneId);
      return false;
    }
    
    if (targetingRules.targeting === 'none') return false;
    
    // Check if unit has instanceId
    if (!unit.instanceId) return false;
    
    // Get player alignment
    const selfId = String(playerId);
    const stateIds = Object.keys(gameState.state || {});
    const opponentId = stateIds.find(id => id !== selfId);
    const normalizedZone = (zone || '').replace('opponent_', '');
    const isOpponentZone = zone.startsWith('opponent_');
    
    // Check if unit is in opponent's or friendly's playedOrders
    const friendlyUnits = (gameState.state[selfId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
    const enemyUnits = (gameState.state[opponentId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
    const isFriendly = friendlyUnits.some(u => u.instanceId === unit.instanceId);
    const isEnemy = enemyUnits.some(u => u.instanceId === unit.instanceId);
    const unitPlayer = isFriendly ? 'friendly' : (isEnemy ? 'opponent' : 'unknown');
    
    // Check player restrictions
    if (targetingRules.player === 'friendly' && unitPlayer !== 'friendly') {
      console.log('🎯 isTarget: Drone requires friendly target but unit is', unitPlayer);
      return false;
    }
    if (targetingRules.player === 'opponent' && unitPlayer !== 'opponent') {
      console.log('🎯 isTarget: Drone requires opponent target but unit is', unitPlayer);
      return false;
    }
    // 'both' allows any unit
    
    // Check zone restrictions
    if (targetingRules.zones && targetingRules.zones !== 'both') {
      const allowedZones = Array.isArray(targetingRules.zones) ? targetingRules.zones : [targetingRules.zones];
      if (!allowedZones.includes(normalizedZone)) {
        console.log('🎯 isTarget: Drone cannot target zone', normalizedZone, 'allowed zones:', allowedZones);
        return false;
      }
    }
    
    console.log('🎯 isTarget: Drone can target unit', unit.name);
    return true;
  }

  // Handle other targeting modes (these use their own logic)
  if (immobilizeTargetMode || confuseTargetMode) {
    return true;
  }
  if (weaponTargetMode) {
    if (unit?.isGeneral) return false;
    // Tutorial overlay: restrict weapon attach to allowed unit order IDs only (e.g. only Opportunist 302)
    const weaponAttachAttr = typeof document !== 'undefined' && document.body && document.body.getAttribute('data-tutorial-overlay-weapon-attach-restrict');
    if (weaponAttachAttr && unit && unit.id != null) {
      try {
        const allowed = JSON.parse(weaponAttachAttr);
        if (Array.isArray(allowed) && allowed.length > 0) {
          const allowedSet = new Set(allowed.map(a => Number(a)));
          if (!allowedSet.has(Number(unit.id))) return false;
        }
      } catch (_) {}
    }
    return true;
  }

  // Handle deploy targeting with database rules
  if (deployTargetMode && selectedDeployOrderId && selectedDeployZone && targetingData) {
    // Tutorial overlay: do not highlight any targets when these deploy orders are selected (e.g. Inferno Drop: no zone/unit highlight for Chief Engineer / Youthful Patriot)
    const deployNoTargetingAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial-overlay-deploy-no-targeting');
    if (deployNoTargetingAttr) {
      try {
        const orderIds = JSON.parse(deployNoTargetingAttr);
        if (Array.isArray(orderIds) && orderIds.includes(selectedDeployOrderId)) return false;
      } catch (_) {}
    }
    // Tutorial overlay: hide front zone for specific deploy orders (e.g. Duplicate: deploy Steel Viper Tank to flank only)
    const deployHideFrontAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial-overlay-deploy-hide-zone-front');
    if (deployHideFrontAttr) {
      try {
        const orderIds = JSON.parse(deployHideFrontAttr);
        const zoneNorm = (zone || '').replace('opponent_', '');
        if (Array.isArray(orderIds) && orderIds.includes(selectedDeployOrderId) && zoneNorm === 'front') return false;
      } catch (_) {}
    }
    // Tutorial 4 deploy target restriction: only allow targeting specific units (optionally scoped to a deploy order, e.g. Front Line Sentinel 309)
    const t4DeployAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial4-deploy-target-restrict');
    if (t4DeployAttr && unit) {
      try {
        const t4 = JSON.parse(t4DeployAttr);
        const appliesToThisDeploy = !t4.deployOrderId || selectedDeployOrderId === t4.deployOrderId;
        if (appliesToThisDeploy && t4.targetOrderId && unit.id !== t4.targetOrderId) return false;
      } catch (_) {}
    }
    // Tutorial overlay: restrict friendly deploy targets to clickableFriendlyZone order IDs (when unit is friendly)
    const overlayTargetingAttr = typeof document !== 'undefined' && document.body.getAttribute('data-tutorial-overlay-targeting');
    if (overlayTargetingAttr && unit) {
      try {
        const overlay = JSON.parse(overlayTargetingAttr);
        const allowed = overlay.clickableFriendlyZone;
        if (Array.isArray(allowed) && allowed.length > 0) {
          const selfId = String(playerId);
          const normalizedZone = (zone || '').replace('opponent_', '');
          const friendlyUnits = (gameState.state?.[selfId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
          const isFriendly = friendlyUnits.some(u => u.instanceId === unit.instanceId);
          if (isFriendly && !allowed.includes(unit.id)) return false;
        }
      } catch (_) {}
    }
    // Get targeting rules for the deployed order
    const targetingRules = getDeployTargetingRules(selectedDeployOrderId, targetingData);
    
    if (!targetingRules) return false;

    // Check if targeting is required
    if (targetingRules.targeting === 'none') return false;

    // Deploy targeting generals (HUD zones)
    if (unit?.isGeneral === true) {
      if (targetingRules.general !== true) return false;
      const pr = targetingRules.player || 'opponent';
      if (zone === 'opponent_general' && (pr === 'opponent' || pr === 'both')) return true;
      if (zone === 'player_general' && (pr === 'friendly' || pr === 'both')) return true;
      return false;
    }

    // Special case: Unit 350 (Enhanced Lion) targets enemy drones
    if (selectedDeployOrderId === 350) {
      // Unit 350 targets enemy drones, not units
      if (unit && unit.type === 'drone') {
        const selfId = String(playerId);
        const stateIds = Object.keys(gameState.state || {});
        const opponentId = stateIds.find(id => id !== selfId);
        
        // Check if this drone belongs to the opponent
        const opponentDrones = gameState.state[opponentId]?.drones || [];
        const isEnemyDrone = opponentDrones.some(drone => drone.id === unit.id || drone.instanceId === unit.instanceId);
        
        if (isEnemyDrone) {
          console.log(`🦁 Unit 350: Highlighting enemy drone ${unit.name} for targeting`);
          return true;
        }
      }
      return false; // Don't highlight units for Unit 350
    }

    // Campaign Mission 3: Kek (239) targeting restrictions controlled by body data attributes
    // data-kek-drone-only: early tutorials → only target enemy drones
    // No attribute: later game (e.g. "Finish the Fight") → allow enemy units for confuse
    if (selectedDeployOrderId === 239 && gameState?.campaignMode && gameState?.missionId === 'chapter_1_mission_3') {
      const kekDroneOnly = typeof document !== 'undefined' && document.body.getAttribute('data-kek-drone-only') === 'true';
      const selfId = String(playerId);
      const stateIds = Object.keys(gameState.state || {});
      const opponentId = stateIds.find(id => id !== selfId);

      if (kekDroneOnly) {
        if (unit && unit.type === 'drone') {
          const opponentDrones = gameState.state[opponentId]?.drones || [];
          const isEnemyDrone = opponentDrones.some(drone => drone.id === unit.id || drone.instanceId === unit.instanceId);
          if (isEnemyDrone) {
            console.log(`🌙 Campaign M3: Kek highlighting enemy drone ${unit.name} for targeting`);
            return true;
          }
        }
        return false;
      }

      // Not in drone-only mode: allow targeting any opponent unit (for confuse)
      if (unit) {
        const normalizedZone = (zone || '').replace('opponent_', '');
        const enemyUnits = (gameState.state[opponentId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
        const isEnemy = enemyUnits.some(u => u.instanceId === unit.instanceId);
        if (isEnemy) {
          console.log(`🌙 Campaign M3: Kek highlighting enemy unit ${unit.name} for confuse targeting`);
          return true;
        }
        if (unit.type === 'drone') {
          const opponentDrones = gameState.state[opponentId]?.drones || [];
          const isEnemyDrone = opponentDrones.some(drone => drone.id === unit.id || drone.instanceId === unit.instanceId);
          if (isEnemyDrone) return true;
        }
        return false;
      }
    }

    // Check zone restrictions based on database rules
    if (targetingRules.zones === 'same') {
      // Can only target the same zone where deployed
      const normalizedZoneForRestriction = (zone || '').replace('opponent_', '');
      if (normalizedZoneForRestriction !== selectedDeployZone) return false;
    } else if (targetingRules.zones === 'other') {
      // Can only target the other zone (not the same zone)
      const normalizedZoneForRestriction = (zone || '').replace('opponent_', '');
      if (normalizedZoneForRestriction === selectedDeployZone) return false;
    } else if (targetingRules.zones === 'both') {
      // Can target any zone (no zone restriction)
      // This is the case for Unit 14
    }

    // Check "affected" field restrictions
    if (targetingRules.affected === 'others' && unit) {
      // Cannot target the deploying unit itself
      // We need to check if this unit is the one being deployed
      // This would require access to the deploying unit's instanceId
      // For now, we'll add this logic in the unit-specific section below
    }

    // DEBUG: Log opponent zone instanceIds seen by the UI during deploy targeting
    try {
      const selfIdDbg = String(playerId);
      const oppIdDbg = Object.keys(gameState.state || {}).find(id => id !== selfIdDbg);
      const normalizedZoneDbg = selectedDeployZone;
      const oppUnitsDbg = (gameState.state[oppIdDbg]?.playedOrders?.[normalizedZoneDbg] || []).filter(Boolean);
      const oppIdsDbg = oppUnitsDbg.map(u => u.instanceId).filter(Boolean);
      console.log('🎯 TARGET_DEBUGGING UI opponent ids in zone', { zone: normalizedZoneDbg, ids: oppIdsDbg });
    } catch (_) {}

    // If a specific unit is provided, check if it can be targeted (by instanceId membership)
    if (unit) {
      // Check both friendly and opponent units based on database rules
      const selfId = String(playerId);
      const stateIds = Object.keys(gameState.state || {});
      const opponentId = stateIds.find(id => id !== selfId);
      // Normalize zone keys for playedOrders access
      const normalizedZone = (zone || '').replace('opponent_', '');
      const friendlyUnits = (gameState.state[selfId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);
      const enemyUnits = (gameState.state[opponentId]?.playedOrders?.[normalizedZone] || []).filter(Boolean);

      const friendlyInstanceIds = friendlyUnits.map(u => u.instanceId).filter(Boolean);
      const enemyInstanceIds = enemyUnits.map(u => u.instanceId).filter(Boolean);

      // Strict: require instanceId presence
      if (!unit.instanceId) {
        try { console.error('TARGETING ERROR: Unit missing instanceId for deploy highlighting', { unitName: unit?.name, unitId: unit?.id, zone, normalizedZone }); } catch (_) {}
        return false;
      }

      const isFriendlyByInstance = friendlyInstanceIds.includes(unit.instanceId);
      const isEnemyByInstance = enemyInstanceIds.includes(unit.instanceId);

      // Strict: do not fallback to id; if not found, report and refuse highlight
      if (!isFriendlyByInstance && !isEnemyByInstance) {
        try { console.error('TARGETING ERROR: Unit instanceId not found in friendly or enemy lists', { unitName: unit?.name, unitId: unit?.id, unitInstanceId: unit?.instanceId, zone, normalizedZone, friendlyCount: friendlyUnits.length, enemyCount: enemyUnits.length }); } catch (_) {}
        return false;
      }

      const isFriendly = isFriendlyByInstance;
      const isEnemy = isEnemyByInstance;
      const unitPlayer = isFriendly ? 'friendly' : (isEnemy ? 'opponent' : 'unknown');

      try {
        console.log('TARGET_DEBUGGING isTarget: check', {
          zone,
          normalizedZone,
          unitName: unit?.name,
          unitId: unit?.id,
          unitInstanceId: unit?.instanceId,
          isFriendlyByInstance,
          isEnemyByInstance,
          isFriendly,
          isEnemy,
          playerId: selfId,
          opponentId,
          friendlyCount: friendlyUnits.length,
          enemyCount: enemyUnits.length,
          friendlySample: friendlyInstanceIds.slice(0, 3),
          enemySample: enemyInstanceIds.slice(0, 3)
        });
      } catch (_) {}

      // Enforce zone restrictions based on database rules
      if (targetingRules.zones === 'same') {
        // Can only target the same zone where deployed
        if (normalizedZone !== selectedDeployZone) return false;
      } else if (targetingRules.zones === 'other') {
        // Can only target the other zone (not the same zone)
        if (normalizedZone === selectedDeployZone) return false;
      } else if (targetingRules.zones === 'both') {
        // Can target any zone (no zone restriction)
        // This is the case for Unit 14
      }

      // Enforce player restrictions based on database rules
      if (targetingRules.player === 'opponent' && !isEnemy) return false;
      if (targetingRules.player === 'friendly' && !isFriendly) return false;
      if (targetingRules.player === 'both') {
        // Can target both friendly and enemy units (no player restriction)
        // This is the case for Unit 14
      }

      // Check "affected" field restrictions
      // Only block self-targeting if affected is explicitly 'others'
      // If affected is 'both', 'self', null, or empty, allow self-targeting
      // ✅ SPECIAL CASE: Unit 349 (Enhanced Scorpion) can always target itself (mandatory targeting)
      // ✅ SPECIAL CASE: Unit 309 (Front Line Sentinel) can always target itself (must shield something)
      if ((selectedDeployOrderId === 349 || selectedDeployOrderId === 309) && selectedDeployInstanceId && unit.instanceId === selectedDeployInstanceId) {
        // Unit 349 or 309 can target itself - this is explicitly allowed
        const unitName = selectedDeployOrderId === 349 ? 'Enhanced Scorpion' : 'Front Line Sentinel';
        console.log(`🎯 TARGETING: Unit ${selectedDeployOrderId} (${unitName}) can target itself`, {
          unitName: unit?.name,
          unitInstanceId: unit?.instanceId,
          selectedDeployInstanceId
        });
        // Continue with normal validation (don't return false)
      } else if (targetingRules.affected && targetingRules.affected.toLowerCase() === 'others') {
        // Cannot target the deploying unit itself when affected='others' (unless it's unit 349 or 309)
        if (selectedDeployInstanceId && unit.instanceId === selectedDeployInstanceId) {
          console.log('🎯 TARGETING: Cannot target deploying unit itself (affected=others)', {
            unitName: unit?.name,
            unitInstanceId: unit?.instanceId,
            selectedDeployInstanceId
          });
          return false;
        }
      }
      // ✅ Unit 349 (Enhanced Scorpion) and other units can target themselves if affected is not 'others'
      // This allows self-targeting when database allows it (affected='both', 'self', null, or empty)

      // Special targeting conditions for specific units
      if (selectedDeployOrderId === 24) {
        // Unit 24 (Hollow Warborn): Can only target units with attack rating <= 2
        if (unit.attack_rating > 2) {
          console.log('🎯 TARGETING: Unit 24 can only target units with attack rating <= 2', {
            unitName: unit?.name,
            unitAttackRating: unit?.attack_rating,
            selectedDeployOrderId
          });
          return false;
        }
      }

      return canTargetUnit({
        targetingRules,
        deployedZone: selectedDeployZone,
        unit,
        unitZone: normalizedZone,
        unitPlayer
      });
    }

    // If no specific unit, check if the zone can be targeted
    return true;
  }

  return false;
}

export default isTarget;
